import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchProjects,
  createProject as apiCreateProject,
  updateProject as apiUpdateProject,
  deleteProject as apiDeleteProject,
  duplicateProject as apiDuplicateProject,
} from "../api/projectsApi";
import {
  fetchCustomers,
  createCustomer as apiCreateCustomer,
  updateCustomer as apiUpdateCustomer,
  deleteCustomer as apiDeleteCustomer,
} from "../api/customersApi";

const emptyList = [];

export function useProjectData(token, onUnauthorized) {
  const [projects, setProjects] = useState(emptyList);
  const [customers, setCustomers] = useState(emptyList);

  const handleAuthError = useCallback(
    async (error) => {
      if (error?.status === 401 && typeof onUnauthorized === "function") {
        await onUnauthorized();
      }
      throw error;
    },
    [onUnauthorized]
  );

  const refreshProjects = useCallback(async () => {
    if (!token) {
      setProjects(emptyList);
      return emptyList;
    }

    try {
      const list = await fetchProjects(token);
      setProjects(list);
      return list;
    } catch (error) {
      await handleAuthError(error);
    }
  }, [token, handleAuthError]);

  const refreshCustomers = useCallback(async () => {
    if (!token) {
      setCustomers(emptyList);
      return emptyList;
    }

    try {
      const list = await fetchCustomers(token);
      setCustomers(list);
      return list;
    } catch (error) {
      await handleAuthError(error);
    }
  }, [token, handleAuthError]);

  // CREATE: API -> refresh (no Excel sync — users don't want auto-created tabs)
  const createProjectWithRefresh = useCallback(
    async (payload) => {
      if (!token) return;

      try {
        const createdProject = await apiCreateProject(token, payload);
        await refreshProjects();
        return createdProject;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, refreshProjects, handleAuthError]
  );

  // UPDATE: API -> refresh (no Excel sync)
  const updateProjectWithRefresh = useCallback(
    async (id, payload) => {
      if (!token) return;

      try {
        await apiUpdateProject(token, id, payload);
        return refreshProjects();
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, refreshProjects, handleAuthError]
  );


  // DUPLICATE: clone project + its whole BOQ tree server-side -> refresh
  const duplicateProjectWithRefresh = useCallback(
    async (id, payload) => {
      if (!token) return;

      try {
        const created = await apiDuplicateProject(token, id, payload);
        await refreshProjects();
        return created;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, refreshProjects, handleAuthError]
  );

  // ✅ DELETE: delete backend -> delete sheet -> refresh
  const deleteProjectWithRefresh = useCallback(
    async (id) => {
      if (!token) return;

      try {
        await apiDeleteProject(token, id);

        // (Later we can delete row from Tier0 if needed)

        await refreshProjects();
        return true;
      } catch (error) {
        await handleAuthError(error);
      }
    },
  )


  const createCustomerWithRefresh = useCallback(
    async (payload) => {
      if (!token) return;

      try {
        const created = await apiCreateCustomer(token, payload);
        await refreshCustomers();
        return created;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, refreshCustomers, handleAuthError]
  );

  const updateCustomerWithRefresh = useCallback(
    async (id, payload) => {
      if (!token) return;

      try {
        const updated = await apiUpdateCustomer(token, id, payload);
        await refreshCustomers();
        return updated;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, refreshCustomers, handleAuthError]
  );

  const deleteCustomerWithRefresh = useCallback(
    async (id) => {
      if (!token) return;

      try {
        await apiDeleteCustomer(token, id);
        await refreshCustomers();
        return true;
      } catch (error) {
        await handleAuthError(error);
      }
    },
    [token, refreshCustomers, handleAuthError]
  );

  useEffect(() => {
    if (!token) {
      setProjects(emptyList);
      setCustomers(emptyList);
    }
  }, [token]);

  return useMemo(
    () => ({
      projects,
      customers,
      refreshProjects,
      refreshCustomers,
      createProject: createProjectWithRefresh,
      updateProject: updateProjectWithRefresh,
      duplicateProject: duplicateProjectWithRefresh,
      deleteProject: deleteProjectWithRefresh,
      createCustomer: createCustomerWithRefresh,
      updateCustomer: updateCustomerWithRefresh,
      deleteCustomer: deleteCustomerWithRefresh,
    }),
    [
      projects,
      customers,
      refreshProjects,
      refreshCustomers,
      createProjectWithRefresh,
      updateProjectWithRefresh,
      duplicateProjectWithRefresh,
      deleteProjectWithRefresh,
      createCustomerWithRefresh,
      updateCustomerWithRefresh,
      deleteCustomerWithRefresh,
    ]
  );
}
